import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
	Space,
	ConfigProvider,
	Form,
	Input,
	Card,
	Row,
	Col,
	Tag,
	Tooltip,
	DatePicker,
	Button
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'

import Title from 'antd/es/typography/Title'
import { runInAction, toJS } from 'mobx'

import { IS_NEW } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	CommentsInput,
	SubtasksInput,
	SubmitButton
} from '../../../components/Form'
import { Features, FormatedTimestamp } from '../../../components/Shared'
import ProgressInput from '../../../components/Form/ProgressInput'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css' // Create a CSS file to handle custom styles
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actLoadMonthlyReport,
	actSaveMonthlyReport,
	requireMonthlyReportsById
} from '../../../stores/monthly-reports/actions'
import dayjs from 'dayjs'
import { TimedSubtasks } from '../../../cls/utils/subtasks/extensions/TimedSubtasks'
import ProgressModal from '../../../components/Form/ProgressModal'
import { useSmartTranslation } from '../../../cls/TranslationContext'
const { MonthPicker } = DatePicker
/**
 * @function MonthlyReportEdit
 * @description Main component for viewing or editing a monthly report. Supports form submission, conditional feature rendering (subtasks/comments), syncing, and tours.
 * @param {boolean} viewOnly - If true, renders the form in read-only mode.
 */
const MonthlyReportEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const { uiLstMonthlyReports, users, tourStore } = useStores()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const _itm = requireMonthlyReportsById(itmId)
	const selfPermission =
		_itm.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin
	// and item not new
	if (!selfPermission && _itm.loaded !== IS_NEW) {
		viewOnly = true
	}

	/**
	 * @function generateInitialFormValues
	 * @description Generates the default values for the form based on the current report state.
	 * @returns {object} Initial form values.
	 */
	const generateInitialFormValues = () => ({
		name: _itm.name ? dayjs.unix(_itm.name) : dayjs().startOf('month'),
		progress: _itm.progress,
		hoursWorked: _itm.hoursWorked,
		subtasks: toJS(_itm.subtasks.list),
		comments: toJS(_itm.comments.list)
	})

	/**
	 * @function _doSave
	 * @description Handles form submission. Saves the report data and redirects back to the monthly reports list on success.
	 * @param {object} vals - The form values to save.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				// Set creator and timestamp
				!_itm.createdBy && _itm.setCreator(loggedInUserId)

				// Reset last full load
				_itm.loaded === IS_NEW && _itm.resetLastFullLoad()

				// Form values
				_itm.setName(vals?.name ? vals?.name.unix() : '')
				_itm.setProgress(vals.progress)
				_itm.setHoursWorked(vals.hoursWorked)

				// Subtasks
				_itm.setSubtasks(vals.subtasks)
				// Comments
				_itm.setComments(vals.comments)
			})

			actSaveMonthlyReport(itmId, toJS(_itm.full))
				.then(() => {
					navigate('/monthly-reports')
				})
				.catch(console.error)
		}
	}

	const [form] = Form.useForm()
	const currentMonth = Form.useWatch('name', form)

	const subtasksTrack = Form.useWatch('subtasks', form)
	useEffect(() => {
		if (subtasksTrack) {
			form.setFieldsValue({
				hoursWorked: subtasksTrack.reduce(
					(sum, task) => sum + (task.hours || 0),
					0
				)
			})
		}
	}, [subtasksTrack])

	// Update form when _itm.modified changes
	useEffect(() => {
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.modified]) // Track _itm.modified

	const [formIsDisabled, setFormIsDisabled] = React.useState(
		!!_itm.to_delete ?? viewOnly
			? true
			: isAdmin
			? false
			: !['draft'].includes(_itm.progress)
	)

	//set default progress value to 'active' is user is admin or superAdmin for new monthly report
	useEffect(() => {
		if (_itm.loaded == IS_NEW && (isAdmin || isSuperAdmin)) {
			form.setFieldsValue({
				progress: 'draft'
			})
		}
	}, [isAdmin, isSuperAdmin])

	// Track changes to _itm.to_delete to update form disabled state and tag visibility
	useEffect(() => {
		setFormIsDisabled(
			viewOnly
				? true
				: !!_itm.to_delete ??
						(isAdmin ? false : !['draft'].includes(_itm.progress))
		)
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.to_delete, viewOnly])

	const [features, setFeatures] = React.useState({
		subtasks: toJS(_itm.subtasks.list)?.length > 0,
		comments: viewOnly ? true : toJS(_itm.comments.list)?.length > 0
	})

	// on features change, set all related fields to empty
	const onFeaturesChange = (features) => {
		if (!features.subtasks) {
			form.setFieldsValue({
				subtasks: []
			})
		}
		if (!features.comments) {
			form.setFieldsValue({
				comments: []
			})
		}
	}

	/**
	 * @function onSetProgressChange
	 * @description Opens the modal to update the report's progress when called.
	 */
	const onSetProgressChange = () => {
		runInAction(() => {
			uiLstMonthlyReports.setModal('monthly-report-progress', {
				visible: true,
				itmId: itmId
			})
		})
	}

	/*
	 * Tour features
	 * This is a tour for the features of the report.
	 */
	const tourFeatures =
		!viewOnly && !formIsDisabled
			? getTourArrayByKeys('features', [
					'section',
					'subtasks',
					'comments'
			  ])
			: []

	const tourForm = getTourArrayByKeys('form', ['section', 'name', 'progress'])

	/**
	 * Start tour:
	 * This function starts the tour for the report form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		tourStore.startTour('MonthlyReportEdit-tour', [
			// Features
			...tourFeatures,

			// General info
			...getTourArrayByKeys('info', ['name', 'modified']),

			// General form
			...getTourArrayByKeys('form', ['section'])
		])
	}

	/**
	 * @function startTourForm
	 * @description Starts the guided UI tour for just the general form section.
	 */
	const startTourForm = () => {
		tourStore.startTour('MonthlyReportEdit-tour', [
			// General form
			...tourForm,
			{
				title: t('_hour.report'),
				description: t('_hour.report-info'),
				target: () => document.getElementById('target-form-hours')
			}
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_monthly-report.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}
								<FormatedTimestamp
									timestamp={_itm.toDeleteTs}
								/>
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			{!viewOnly && !formIsDisabled && (
				<Features
					features={features}
					setFeatures={(val) => {
						setFeatures(val)
						onFeaturesChange(val)
					}}
				/>
			)}
			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_monthly-report')}>
								<EyeOutlined
									className="no-print"
									id="target-mode"
								/>{' '}
								{dayjs.unix(_itm.name)?.format('YYYY-MM')}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new%_monthly-report')
						) : (
							<Tooltip title={t('__edit%_monthly-report')}>
								<SettingOutlined />{' '}
								{dayjs.unix(_itm.name)?.format('YYYY-MM')}
							</Tooltip>
						)}{' '}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ProgressModal
					type="monthly-report"
					CustomClass={TimedSubtasks}
					modalKey="monthly-report-progress"
					uiStore={uiLstMonthlyReports}
					requireItemById={requireMonthlyReportsById}
					loadItm={(id) => actLoadMonthlyReport(id)}
					saveItem={(itemId, itemValue) => {
						actSaveMonthlyReport(itemId, itemValue)
						form.setFieldsValue({
							progress: _itm.progress
						})
					}}
				/>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="report"
						initialValues={generateInitialFormValues()}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Row
							gutter={[50, 50]}
							style={
								features.subtasks && {
									borderBottom: '1px solid #f0f0f0'
								}
							}
						>
							<Col
								id="target-form"
								className="section-pattern-dots"
								span={features.recurrence ? 12 : 24}
								style={{
									// backgroundColor: 'grey',
									...(features.recurrence
										? {
												borderRight:
													'1px solid #f0f0f0',
												paddingRight: '25px'
										  }
										: {})
								}}
							>
								<Space
									style={{ width: '100%' }}
									direction="vertical"
								>
									<GeneralTourTitle
										align="end"
										titleProps={{
											text: 'General',
											h: Title,
											level: 5
										}}
										buttonProps={{
											onClick: startTourForm,
											disabled: false,
											style: { marginBottom: 4 }
										}}
									/>
									<div id="target-form-name">
										<Form.Item
											label={t('single.name')}
											tooltip={{
												title: t(
													'single.search.description'
												),
												icon: <InfoCircleOutlined />
											}}
											name="name"
											rules={[
												{
													required: true,
													message: t(
														'__provide.please%single.name'
													)
												}
											]}
										>
											<MonthPicker
												style={{ minWidth: '150px' }}
												disabled={formIsDisabled}
											/>
										</Form.Item>
									</div>
									<Space style={{ width: '100%' }}>
										<div id="target-form-hours">
											<Form.Item
												label={t('_hour.worked')}
												tooltip={{
													title: t(
														'_hour.worked-total'
													),
													icon: <InfoCircleOutlined />
												}}
												name="hoursWorked"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.name'
														)
													}
												]}
											>
												<Input disabled={true} />
											</Form.Item>
										</div>
									</Space>
									<div id="target-form-progress">
										<Row align="bottom" gutter={6}>
											<Col>
												<Form.Item
													label={t('single.progress')}
													tooltip={{
														title: t(
															'single.curent-progress'
														),
														icon: (
															<InfoCircleOutlined />
														)
													}}
													name="progress"
													rules={[
														{
															required: true,
															message: t(
																'single.progress-required'
															)
														}
													]}
												>
													<ProgressInput
														type="monthly-report"
														viewOnly={viewOnly}
														disabled={
															formIsDisabled
														}
													/>
												</Form.Item>
											</Col>
											{viewOnly &&
												(selfPermission ||
													users.canUser(
														'write',
														'monthly_reports'
													)) && (
													<Col>
														<Form.Item>
															<Button
																className="no-print"
																type="primary"
																onClick={(
																	e
																) => {
																	e.preventDefault()
																	onSetProgressChange()
																}}
																ghost
															>
																Set Progress
															</Button>
														</Form.Item>
													</Col>
												)}
										</Row>
									</div>
								</Space>
							</Col>
						</Row>
						<Row
							gutter={[50, 50]}
							style={
								!viewOnly && {
									borderBottom: '1px solid #f0f0f0'
								}
							}
						>
							{features.subtasks && (
								<Col
									span={features.comments ? 12 : 24}
									className="print-only-width-full"
									style={
										features.comments && {
											borderRight: '1px solid #f0f0f0',
											paddingRight: '25px'
										}
									}
								>
									<Title level={5}>
										{t('_subtask.multiple')}
									</Title>
									<Form.Item
										label={t('__items%_subtask.multiple')}
										name="subtasks"
										tooltip={{
											title: t(
												'__x-can-also-have-x%_subtask.multiple%_comment.multiple'
											),
											icon: <InfoCircleOutlined />
										}}
									>
										<SubtasksInput
											displaySubtaskDescription
											CustomClass={TimedSubtasks}
											createdBy={loggedInUserId}
											disabled={formIsDisabled}
											currentMonth={currentMonth}
										/>
									</Form.Item>
								</Col>
							)}
							{features.comments && (
								<Col
									className={
										!_itm.comments.list.length
											? 'no-print'
											: 'print-only-width-full'
									}
									span={features.subtasks ? 12 : 24}
								>
									<Title level={5}>
										{t('_comment.multiple')}
									</Title>
									<Form.Item
										label={t('_comment.multiple')}
										name="comments"
										tooltip={{
											title: t(
												'__add-allowed%_comment.multiple%_report'
											),
											icon: <InfoCircleOutlined />
										}}
									>
										<CommentsInput
											createdBy={loggedInUserId}
											disabled={
												!selfPermission &&
												!users.canUser(
													'write',
													'monthly_reports'
												)
											}
										/>
									</Form.Item>
								</Col>
							)}
						</Row>
						{/* {!viewOnly && ( */}
						<Row
							gutter={[10, 10]}
							align="bottom"
							className="no-print"
						>
							<Col>
								<SubmitButton
									disabled={
										!selfPermission &&
										!users.canUser(
											'write',
											'monthly_reports'
										)
									}
								>
									{_itm.loaded === IS_NEW
										? t('single.create')
										: t('single.update')}
								</SubmitButton>
							</Col>
							{
								// Show  cancel button only if editing resource
								!viewOnly && (
									<Col>
										<Button
											type="default"
											onClick={() => navigate(-1)}
										>
											{t('single.cancel')}
										</Button>
									</Col>
								)
							}
							{viewOnly && (
								<Col>
									<Button
										type="default"
										onClick={() => window.print()}
									>
										{t('__download%single.pdf')}
									</Button>
								</Col>
							)}
						</Row>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}
MonthlyReportEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(MonthlyReportEdit)
